// apps/flux-ev-web/src/pages/FluxEVApp.js
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Routes, Route, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    Alert,
} from '@mui/material';
import {
    DataUsage as DataUsageIcon,
    Assessment as ResultsIcon,
    MonetizationOn as VppIcon,
} from '@mui/icons-material';
import { useAuth, useThemeMode, useRetroContextSafe, VPPRevenueTab } from '@vortx/shared-ui';
// Import from parent shell to share same Context instance
import { ExportDialog } from '../components/ev';
import { retroContainerStyles } from './fluxEVStyles';
import { Skeleton } from '../components/ux/Skeleton';

// Feature module
import {
    useEVProject,
    useEVScenarios,
    useEVSimulation,
    EVProjectDashboard,
    EVProjectHeader,
    DepotUploadSection,
    DepotResultsSection,
} from '../features/ev';
import {
    ScenariosSection,
} from '../features/ev/sections';

/**
 * FluxEV - EV Charging Modeling App (Internal Component)
 *
 * Thin page shell composing hooks and feature module sections.
 * Fleet projects show ScenariosSection directly (scenarios are the entry point).
 * Depot projects retain upload/results navigation.
 */
function FluxEVInternal() {
    const { projectId, section: urlSection, scenarioId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { mode } = useThemeMode();
    const isRetro = mode === 'retro';

    // RetroContext for section navigation (syncs with sidebar)
    const { evSection, setEvSection, setEvProjectType, evScenarioTab, setEvScenarioTab } = useRetroContextSafe();

    // Section transition
    const [sectionTransitioning, setSectionTransitioning] = useState(false);
    const prevSectionRef = useRef(null);

    // Derive active section from URL, synced to context for sidebar
    const activeSection = urlSection || evSection || 'scenarios';

    // Section transition effect - must be before any early returns (Rules of Hooks)
    useEffect(() => {
        if (prevSectionRef.current && prevSectionRef.current !== activeSection) {
            setSectionTransitioning(true);
            const timer = setTimeout(() => {
                setSectionTransitioning(false);
                prevSectionRef.current = activeSection;
            }, 150);
            return () => clearTimeout(timer);
        }
        if (!prevSectionRef.current) {
            prevSectionRef.current = activeSection;
        }
    }, [activeSection]);

    // Sync URL section to context so the retro sidebar highlights correctly
    useEffect(() => {
        if (urlSection && urlSection !== evSection) {
            setEvSection(urlSection);
        }
    }, [urlSection, evSection, setEvSection]);

    // Navigate to section via URL
    const navigateToSection = useCallback((sectionId) => {
        if (!projectId) return;
        navigate(`/ev/${projectId}/${sectionId}`, { replace: true });
        setEvSection(sectionId);
    }, [projectId, navigate, setEvSection]);

    // Compose hooks
    const project = useEVProject(projectId);
    const scenarios = useEVScenarios(projectId, project.currentProject);
    const simulation = useEVSimulation(projectId);

    // Sync URL scenarioId → selected scenario
    useEffect(() => {
        if (scenarioId && scenarios.scenarios.length > 0) {
            const target = scenarios.scenarios.find(s => s.id === scenarioId);
            if (target && scenarios.selectedScenario?.id !== scenarioId) {
                scenarios.selectScenario(target);
            }
        } else if (!scenarioId && scenarios.selectedScenario) {
            scenarios.deselectScenario();
        }
    }, [scenarioId, scenarios.scenarios]); // eslint-disable-line react-hooks/exhaustive-deps

    // Navigate to scenario URL (used by ScenariosSection)
    // Also select immediately so the editor renders without waiting for URL-sync effect
    const navigateToScenario = useCallback((scenario) => {
        scenarios.selectScenario(scenario);
        navigate(`/ev/${projectId}/scenarios/${scenario.id}`);
    }, [projectId, navigate, scenarios.selectScenario]);

    // Navigate back from scenario editor
    const navigateBackFromScenario = useCallback(() => {
        navigate(`/ev/${projectId}/scenarios`);
    }, [projectId, navigate]);

    // Read tab from URL query param
    const [searchParams] = useSearchParams();
    const urlTab = searchParams.get('tab');
    useEffect(() => {
        if (urlTab !== null) {
            const tabIndex = parseInt(urlTab, 10);
            if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 3) {
                setEvScenarioTab(tabIndex);
            }
        }
    }, [urlTab, setEvScenarioTab]);

    // Set project type in context for conditional navigation
    // Redirect to default section if URL section is invalid for this project type
    // Skip redirect when inside a scenario editor (scenarioId in URL)
    useEffect(() => {
        if (project.currentProject) {
            const projectType = project.currentProject.project_type || 'fleet';
            setEvProjectType(projectType);

            // Don't redirect when inside a scenario editor — scenarioId route
            // doesn't have a :section param so urlSection is undefined, but that's expected
            if (scenarioId) return;

            // Determine valid sections for this project type
            const validSections = projectType === 'depot'
                ? ['upload', 'results', 'vpp']
                : ['scenarios', 'vpp'];

            const currentSection = urlSection || evSection;
            if (!urlSection || !currentSection || !validSections.includes(currentSection)) {
                // Always ensure section is in the URL for refresh support
                const targetSection = (currentSection && validSections.includes(currentSection))
                    ? currentSection
                    : (projectType === 'depot' ? 'upload' : 'scenarios');
                if (projectId) {
                    navigate(`/ev/${projectId}/${targetSection}`, { replace: true });
                }
                setEvSection(targetSection);
            }
        }
    }, [project.currentProject, setEvProjectType, urlSection, evSection, setEvSection, projectId, navigate, scenarioId]);

    // Clear project type when returning to dashboard
    useEffect(() => {
        if (!projectId) {
            setEvProjectType(null);
        }
    }, [projectId, setEvProjectType]);

    // Export dialog
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const handleExportSuccess = useCallback((result) => {
        console.log('Export successful:', result);
    }, []);

    if (project.loading) {
        const skBg = isRetro ? 'rgba(255,255,255,0.08)' : undefined;
        const skBgLight = isRetro ? 'rgba(255,255,255,0.05)' : undefined;
        return (
            <Box sx={isRetro ? retroContainerStyles : {}}>
                {/* Header: "Flux EV" */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Skeleton variant="text" width={120} sx={{ height: 40, bgcolor: skBg }} />
                </Box>
                {/* Tab bar */}
                <Box sx={{ display: 'flex', gap: 3, mb: 3, borderBottom: '1px solid', borderColor: isRetro ? 'rgba(63,106,100,0.3)' : 'divider', pb: 1 }}>
                    <Skeleton variant="text" width={140} sx={{ height: 28, bgcolor: skBg }} />
                    <Skeleton variant="text" width={140} sx={{ height: 28, bgcolor: skBgLight }} />
                </Box>
                {/* Description + button row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Skeleton variant="text" width="50%" sx={{ height: 18, bgcolor: skBgLight }} />
                    <Skeleton variant="rectangular" width={140} height={36} sx={{ borderRadius: 1, bgcolor: skBg }} />
                </Box>
                {/* Table rows */}
                <Box sx={{
                    border: '1px solid',
                    borderColor: isRetro ? 'rgba(63,106,100,0.3)' : 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                }}>
                    {/* Table header */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5,
                        bgcolor: isRetro ? 'rgba(0,0,0,0.3)' : 'action.hover',
                    }}>
                        <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: skBgLight, flexShrink: 0 }} />
                        <Skeleton variant="text" width={80} sx={{ height: 16, bgcolor: skBgLight }} />
                        <Box sx={{ flex: 1 }} />
                        <Skeleton variant="text" width={60} sx={{ height: 16, bgcolor: skBgLight }} />
                        <Skeleton variant="text" width={60} sx={{ height: 16, bgcolor: skBgLight }} />
                    </Box>
                    {/* Table rows */}
                    {[1, 2, 3].map((i) => (
                        <Box key={i} sx={{
                            display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5,
                            borderTop: '1px solid',
                            borderColor: isRetro ? 'rgba(63,106,100,0.2)' : 'divider',
                            animation: `fadeIn 0.3s ease-out ${i * 0.08}s both`,
                        }}>
                            <Skeleton variant="circular" width={28} height={28} sx={{ bgcolor: skBgLight, flexShrink: 0 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
                                <Skeleton variant="circular" width={20} height={20} sx={{ bgcolor: skBg, flexShrink: 0 }} />
                                <Skeleton variant="text" width={120 + i * 20} sx={{ height: 20, bgcolor: skBg }} />
                            </Box>
                            <Skeleton variant="text" width={180} sx={{ height: 16, bgcolor: skBgLight, flex: 1 }} />
                            <Skeleton variant="rectangular" width={56} height={28} sx={{ borderRadius: 1, bgcolor: skBg, flexShrink: 0 }} />
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    // Dashboard view (no project selected)
    if (!projectId) {
        return (
            <EVProjectDashboard
                projects={project.projects}
                error={project.error}
                onCreateProject={project.createProject}
                onClearError={project.clearError}
                onDeleteProject={project.deleteProject}
                onRenameProject={project.renameProject}
            />
        );
    }

    // Project view - section navigation + content
    // Wait for project to load before determining type
    if (!project.currentProject) {
        // Show error if project fetch failed, otherwise show loading skeleton
        if (project.error) {
            return (
                <Box sx={isRetro ? retroContainerStyles : { p: 3 }}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {project.error}
                    </Alert>
                </Box>
            );
        }
        return (
            <Box sx={isRetro ? retroContainerStyles : { p: 3 }}>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
            </Box>
        );
    }

    const projectType = project.currentProject.project_type || 'fleet';

    const depotSections = [
        { id: 'upload', label: 'Upload', icon: <DataUsageIcon /> },
        { id: 'results', label: 'Results', icon: <ResultsIcon /> },
        { id: 'vpp', label: 'VPP Revenue', icon: <VppIcon /> },
    ];

    return (
        <Box sx={isRetro ? retroContainerStyles : {}}>
            <EVProjectHeader project={project} />

            {project.error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={project.clearError}>
                    {project.error}
                </Alert>
            )}

            {/* Section navigation in light mode */}
            {!isRetro && projectType === 'depot' && (
                <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                    {depotSections.map(section => (
                        <Button
                            key={section.id}
                            variant={activeSection === section.id ? 'contained' : 'outlined'}
                            startIcon={section.icon}
                            onClick={() => navigateToSection(section.id)}
                            size="small"
                        >
                            {section.label}
                        </Button>
                    ))}
                </Box>
            )}
            {!isRetro && projectType === 'fleet' && (
                <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                    <Button
                        variant={activeSection !== 'vpp' ? 'contained' : 'outlined'}
                        startIcon={<DataUsageIcon />}
                        onClick={() => navigateToSection('scenarios')}
                        size="small"
                    >
                        Scenarios
                    </Button>
                    <Button
                        variant={activeSection === 'vpp' ? 'contained' : 'outlined'}
                        startIcon={<VppIcon />}
                        onClick={() => navigateToSection('vpp')}
                        size="small"
                    >
                        VPP Revenue
                    </Button>
                </Box>
            )}

            {/* Section content */}
            <Box sx={{
                transition: 'opacity 0.2s ease, transform 0.2s ease',
                opacity: sectionTransitioning ? 0 : 1,
                transform: sectionTransitioning ? 'translateY(4px)' : 'translateY(0)',
            }}>
                {/* Fleet projects: ScenariosSection is the default view */}
                {projectType === 'fleet' && activeSection !== 'vpp' && (
                    <ScenariosSection
                        scenarios={scenarios}
                        siteCapacity={project.siteCapacity}
                        projectData={project.currentProject}
                        onSelectScenario={navigateToScenario}
                        onBack={navigateBackFromScenario}
                        activeTab={evScenarioTab}
                    />
                )}

                {projectType === 'depot' && (
                    <>
                        {activeSection === 'upload' && (
                            <DepotUploadSection
                                projectId={projectId}
                                depotConfig={project.currentProject?.depot_config}
                                onComplete={(result) => {
                                    // Reload project data to get updated depot_config
                                    project.reloadProject();
                                    // Navigate to results page after successful decomposition
                                    navigateToSection('results');
                                }}
                            />
                        )}

                        {activeSection === 'results' && (
                            <DepotResultsSection project={project.currentProject} />
                        )}
                    </>
                )}

                {/* VPP Revenue — available for both fleet and depot projects */}
                {activeSection === 'vpp' && (
                    <VPPRevenueTab
                        project={project.currentProject}
                        currentUser={currentUser}
                    />
                )}

            </Box>

            {/* Export Dialog */}
            <ExportDialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                evProjectId={projectId}
                evProjectName={project.currentProject?.name || ''}
                onSuccess={handleExportSuccess}
            />
        </Box>
    );
}

/**
 * FluxEVApp - Routing wrapper for FluxEV
 *
 * Handles routing for EV projects:
 * - /ev → Dashboard (no projectId)
 * - /ev/:projectId → Project view
 */
function FluxEVApp() {
    return (
        <Routes>
            <Route path="/" element={<FluxEVInternal />} />
            <Route path="/:projectId" element={<FluxEVInternal />} />
            <Route path="/:projectId/:section" element={<FluxEVInternal />} />
            <Route path="/:projectId/scenarios/:scenarioId" element={<FluxEVInternal />} />
        </Routes>
    );
}

export default FluxEVApp;
